import * as z from 'zod';

// Define the form schema with Zod
export const formSchema = z.object({
  // Step 1: Project Basics
  full_name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  company_name: z.string().optional(),
  project_type: z.enum(["web", "mobile", "desktop", "other"]),
  
  // Step 2: Features
  project_description: z.string().min(20, "Please provide more details about your project"),
  features: z.array(z.string()).optional(),
  has_design: z.boolean().default(false),
  design_references: z.array(z.string()).optional(),
  
  // Step 3: Timeline
  timeline: z.enum(["1-3 months", "3-6 months", "6+ months"]),
  budget_range: z.enum(["<$3k", "$3k-$10k", "$10k-$50k", "$50k+"]),
  start_date: z.date(),
  
  // Bot protection fields
  honeypot: z.string().max(0, "This field should be empty").optional(),
  form_start_time: z.number().optional(),
});

// Define the type for our form values
export type FormValues = z.infer<typeof formSchema>;

// Define the default values for our form
export const defaultValues: Partial<FormValues> = {
  full_name: "",
  email: "",
  company_name: "",
  project_type: "web",
  project_description: "",
  features: [],
  has_design: false,
  design_references: [],
  timeline: undefined,
  budget_range: undefined,
  honeypot: "",
  form_start_time: Date.now(),
};

// Define types for project types
export const projectTypes = [
  { value: "web", label: "Web Application" },
  { value: "mobile", label: "Mobile App" },
  { value: "desktop", label: "Desktop Software" },
  { value: "other", label: "Other / Custom" },
];

// Define types for timeline options
export const timelineOptions = [
  { value: "1-3 months", label: "1-3 months" },
  { value: "3-6 months", label: "3-6 months" },
  { value: "6+ months", label: "6+ months" },
];

// Define types for budget range options
export const budgetRangeOptions = [
  { value: "<$3k", label: "Less than $3,000" },
  { value: "$3k-$10k", label: "$3,000 - $10,000" },
  { value: "$10k-$50k", label: "$10,000 - $50,000" },
  { value: "$50k+", label: "$50,000+" },
];

// Define feature categories
export const featureCategories = [
  {
    category: "User Interface",
    options: [
      "Responsive Design",
      "Dark Mode",
      "Custom Theme",
      "Interactive Components",
      "Animations",
    ],
  },
  {
    category: "Functionality",
    options: [
      "User Authentication",
      "File Uploads",
      "Payment Processing",
      "Real-time Updates",
      "Notifications",
    ],
  },
  {
    category: "Integration",
    options: [
      "Third-party API Integration",
      "Social Media Integration",
      "Analytics",
      "Email Service",
      "Content Management System",
    ],
  },
];
